import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {initReactI18next} from 'react-i18next';
import {localeAliases, resources, type SupportedLocale} from './generated';

const fallbackLocale: SupportedLocale = 'en';
const supportedLocales = Object.keys(resources) as SupportedLocale[];

function resolveAlias(language: string): SupportedLocale | undefined {
  const alias = localeAliases[language.toLowerCase() as keyof typeof localeAliases];
  if (alias) {
    return alias;
  }

  if (supportedLocales.includes(language as SupportedLocale)) {
    return language as SupportedLocale;
  }

  return undefined;
}

function normalizeLocale(language?: string): SupportedLocale {
  if (!language) {
    return fallbackLocale;
  }

  return resolveAlias(language) ?? fallbackLocale;
}

// Used as i18next's `convertDetectedLanguage`. Unlike `normalizeLocale`, this
// must NOT coerce an unrecognised code to the fallback locale: doing so would
// make an unsupported `?lang=` (or an unsupported navigator language) look
// like a match against `supportedLngs`, short-circuiting the detector chain
// before it can fall through to localStorage/navigator/htmlTag - and then
// `caches: ['localStorage']` would pin that wrong match for future visits.
// Returning the raw, unresolved code instead lets i18next's own
// `supportedLngs` filter reject it and keep looking.
function convertDetectedLanguage(language: string): string {
  return resolveAlias(language) ?? language;
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: fallbackLocale,
    supportedLngs: supportedLocales,
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['querystring', 'localStorage', 'navigator', 'htmlTag'],
      lookupQuerystring: 'lang',
      lookupLocalStorage: 'vrittiLandingLng',
      caches: ['localStorage'],
      convertDetectedLanguage,
    },
  });

export function getCanonicalLocale(language?: string): SupportedLocale {
  return normalizeLocale(language);
}

export default i18n;
